import React from "react";
import { Link } from "react-router-dom";
import WhatsApp from "../inc/Whatsapp";
import {
  FaArrowRight,
  FaChevronRight,
  FaBrain,
  FaNetworkWired,
  FaMapMarkerAlt,
  FaCloud,
  FaShieldAlt,
  FaLightbulb,
  FaUsers,
  FaRocket,
  FaCheckCircle,
} from "react-icons/fa";
import VMC    from "./inc/Vmc";
import Leader from "../inc/Leader";

/* ─────────────────────────────────────────────────────────────
   Brand constant
───────────────────────────────────────────────────────────── */
const COMPANY = "Skywise Technologies";

/* ─────────────────────────────────────────────────────────────
   Data
───────────────────────────────────────────────────────────── */
const STATS = [
  { id: "st1", value: "10+", label: "Projects Delivered"  },
  { id: "st2", value: "4+", label: "Enterprise Clients"  },
  { id: "st3", value: "6+",  label: "Tech Domains"        },
  { id: "st4", value: "99%", label: "Client Satisfaction" },
];

const TECH_TAGS = [
  { id: "tt1", icon: <FaBrain />,        label: "Artificial Intelligence" },
  { id: "tt2", icon: <FaNetworkWired />, label: "IoT & Sensors"           },
  { id: "tt3", icon: <FaMapMarkerAlt />, label: "GPS Fleet Tracking"      },
  { id: "tt4", icon: <FaCloud />,        label: "Cloud Computing"         },
];

const PILLARS = [
  { id: "p1", icon: <FaShieldAlt />, title: "Reliability", desc: "Enterprise-grade systems built to perform 24/7 with zero tolerance for downtime.",                        accent: "#2563eb", bg: "rgba(37,99,235,0.07)"  },
  { id: "p2", icon: <FaLightbulb />, title: "Innovation",  desc: "We stay ahead of industry curves by continuously adopting emerging technologies.",                        accent: "#7c3aed", bg: "rgba(124,58,237,0.07)" },
  { id: "p3", icon: <FaUsers />,     title: "Partnership", desc: "We treat every client as a long-term partner, not a one-time project.",                                  accent: "#059669", bg: "rgba(5,150,105,0.07)"  },
  { id: "p4", icon: <FaRocket />,    title: "Speed",       desc: "Agile delivery methodology ensures you go from concept to production rapidly.",                          accent: "#dc2626", bg: "rgba(220,38,38,0.07)"  },
];

const QUOTE_CHECKS = [
  { id: "qc1", text: "Real-time fleet visibility & management"  },
  { id: "qc2", text: "AI-driven analytics and reporting"        },
  { id: "qc3", text: "Seamless ERP integration & automation"    },
];

const PARAGRAPHS = [
  { id: "para1", content: (
    <><strong>{COMPANY} LLP</strong> is an innovation-driven company focused on building intelligent,
    reliable, and scalable software solutions for the mining and logistics industries.</>
  )},
  { id: "para2", content: (
    <>With a vision to transform complex operations through technology, we build tailored applications
    that enhance efficiency, improve safety, safeguard assets, and empower smarter, data-backed
    decisions at every level of an organisation.</>
  )},
  { id: "para3", content: (
    <>Our strength lies in uniting domain expertise with next-generation technologies like{" "}
    <span className="ab-highlight">AI, IoT, GPS-based tracking, and Cloud Computing</span>
    {" "}— delivering real-time fleet management, logistics automation, and mining productivity
    optimisation.</>
  )},
];

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
function About() {
  return (
    <>
      <style>{`
        /*
         * System font stack — no external download.
         *   macOS / iOS  → San Francisco (-apple-system)
         *   Windows      → Segoe UI
         *   Android      → Roboto
         *   Fallback     → Helvetica → Arial → sans-serif
         *
         * Both --font-display (headings) and --font-body (body)
         * use the same stack for a clean, unified look.
         */

        :root {
          --ab-accent:       #2563eb;
          --ab-accent2:      #00c2ff;
          --ab-bg:           #f0f4f9;
          --ab-card:         #ffffff;
          --ab-text-dark:    #0f172a;
          --ab-text-body:    #475569;
          --ab-text-muted:   #94a3b8;
          --ab-border:       #e2e8f0;
          --ab-shadow:       0 4px 24px rgba(0,0,0,0.07);
          --ab-shadow-hover: 0 10px 36px rgba(0,0,0,0.13);
          --ab-radius:       16px;

          --font-display: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          --font-body:    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        }

        .ab-page {
          background: var(--ab-bg);
          font-family: var(--font-body);
          min-height: 100vh;
        }

        /* ── Hero ── */
        .ab-hero {
          position: relative;
          background: linear-gradient(135deg, #060f24 0%, #0d2352 50%, #091a3e 100%);
          min-height: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .ab-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 48px 48px;
        }
        .ab-hero::after {
          content: '';
          position: absolute;
          top: -80px; left: 10%;
          width: 420px; height: 420px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%);
          pointer-events: none;
        }
        .ab-hero-glow2 {
          position: absolute;
          bottom: -100px; right: 8%;
          width: 340px; height: 340px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,194,255,0.16) 0%, transparent 70%);
          pointer-events: none;
        }
        .ab-hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 4rem 1.5rem;
        }

        /* Eyebrow badge */
        .ab-hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-family: var(--font-body);
          font-size: 0.74rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ab-accent2);
          background: rgba(0,194,255,0.1);
          border: 1px solid rgba(0,194,255,0.22);
          padding: 0.3rem 0.95rem;
          border-radius: 50px;
          margin-bottom: 1.1rem;
        }
        .ab-hero-eyebrow::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--ab-accent2);
          animation: ab-pulse 1.8s infinite;
        }
        @keyframes ab-pulse {
          0%, 100% { opacity: 1;  transform: scale(1);   }
          50%       { opacity: .4; transform: scale(.7);  }
        }

        /* Hero heading */
        .ab-hero-title {
          font-family: var(--font-display);
          font-size: clamp(2rem, 5vw, 3rem);
          font-weight: 600;
          color: #fff;
          margin: 0 0 0.6rem;
          line-height: 1.3;
        }
        .ab-hero-title span {
          background: linear-gradient(90deg, var(--ab-accent2), #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ab-hero-sub {
          font-family: var(--font-body);
          font-size: 1rem;
          font-weight: 400;
          color: rgba(255,255,255,0.65);
          max-width: 500px;
          margin: 0 auto;
          line-height: 1.75;
        }

        /* ── Container ── */
        .ab-container {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 1.5rem 5rem;
        }

        /* ── Floating card ── */
        .ab-card-wrap {
          background: var(--ab-card);
          border-radius: var(--ab-radius);
          box-shadow: var(--ab-shadow);
          margin-top: -52px;
          position: relative;
          z-index: 2;
          overflow: hidden;
        }

        /* ── Breadcrumb ── */
        .ab-breadcrumb {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.9rem 2rem;
          border-bottom: 1px solid var(--ab-border);
          font-family: var(--font-body);
          font-size: 0.82rem;
          color: var(--ab-text-muted);
        }
        .ab-breadcrumb a {
          color: var(--ab-accent);
          text-decoration: none;
          font-weight: 500;
        }
        .ab-breadcrumb a:hover { text-decoration: underline; }
        .ab-breadcrumb svg { font-size: 0.55rem; opacity: 0.5; }

        /* ── Card body ── */
        .ab-card-body { padding: 2.5rem 2rem; }

        /* ── Eyebrow / section title ── */
        .ab-eyebrow {
          font-family: var(--font-body);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--ab-accent);
          margin-bottom: 0.35rem;
        }
        .ab-section-title {
          font-family: var(--font-display);
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 600;
          color: var(--ab-text-dark);
          margin: 0 0 0.4rem;
          line-height: 1.3;
        }
        .ab-underline {
          width: 48px; height: 3px;
          background: var(--ab-accent);
          border-radius: 2px;
          margin-bottom: 1.5rem;
        }

        /* ── Company intro grid ── */
        .ab-intro-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 2.5rem;
          align-items: flex-start;
          margin-bottom: 2.5rem;
        }
        .ab-intro-text { flex: 1 1 340px; }
        .ab-intro-text p {
          font-family: var(--font-body);
          font-size: 0.97rem;
          font-weight: 400;
          color: var(--ab-text-body);
          line-height: 1.8;
          margin-bottom: 1.1rem;
        }
        .ab-intro-text p:last-child { margin-bottom: 0; }
        .ab-highlight {
          color: var(--ab-accent);
          font-weight: 600;
        }

        /* Stats panel */
        .ab-stats-panel {
          flex: 0 1 260px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: var(--ab-border);
          border: 1px solid var(--ab-border);
          border-radius: 12px;
          overflow: hidden;
        }
        .ab-stat {
          background: var(--ab-card);
          padding: 1.25rem 1rem;
          text-align: center;
        }
        .ab-stat-value {
          font-family: var(--font-display);
          font-size: 1.8rem;
          font-weight: 600;
          line-height: 1;
          margin-bottom: 0.25rem;
          background: linear-gradient(90deg, var(--ab-accent), #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ab-stat-label {
          font-family: var(--font-body);
          font-size: 0.74rem;
          font-weight: 500;
          color: var(--ab-text-muted);
          line-height: 1.3;
        }

        /* ── Tech tags ── */
        .ab-tech-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 0.65rem;
          margin-bottom: 2.5rem;
          padding-bottom: 2.5rem;
          border-bottom: 1px solid var(--ab-border);
        }
        .ab-tech-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.42rem 0.95rem;
          border-radius: 8px;
          background: rgba(37,99,235,0.07);
          border: 1px solid rgba(37,99,235,0.18);
          color: var(--ab-accent);
          font-family: var(--font-body);
          font-size: 0.84rem;
          font-weight: 600;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
        }
        .ab-tech-tag:hover {
          background: rgba(37,99,235,0.13);
          box-shadow: 0 4px 14px rgba(37,99,235,0.15);
          transform: translateY(-2px);
        }
        .ab-tech-tag svg { font-size: 0.8rem; }

        /* ── Quote / highlight band ── */
        .ab-quote-band {
          background: linear-gradient(105deg, #060f24 0%, #0d2352 100%);
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2.5rem;
          position: relative;
          overflow: hidden;
        }
        .ab-quote-band::before {
          content: '';
          position: absolute;
          top: -40px; right: -40px;
          width: 200px; height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,194,255,0.18) 0%, transparent 70%);
        }
        .ab-quote-band-inner {
          position: relative;
          z-index: 1;
          display: flex;
          flex-wrap: wrap;
          gap: 2rem;
          align-items: center;
        }
        .ab-quote-left { flex: 0 1 260px; }
        .ab-quote-left h4 {
          font-family: var(--font-display);
          font-size: 1.1rem;
          font-weight: 500;
          color: rgba(255,255,255,0.55);
          line-height: 1.55;
          margin: 0;
        }
        .ab-quote-left h4 strong {
          color: var(--ab-accent2);
          display: block;
          font-size: 1.2rem;
          font-weight: 600;
        }
        .ab-quote-divider {
          width: 1px;
          align-self: stretch;
          background: rgba(255,255,255,0.12);
          flex-shrink: 0;
        }
        .ab-quote-right { flex: 1 1 280px; }
        .ab-quote-right p {
          font-family: var(--font-body);
          font-size: 0.97rem;
          font-weight: 400;
          color: rgba(255,255,255,0.72);
          line-height: 1.75;
          margin: 0 0 1rem;
        }
        .ab-quote-right p strong { color: #fff; font-weight: 600; }
        .ab-quote-check {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--font-body);
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--ab-accent2);
          margin-bottom: 0.4rem;
        }
        .ab-quote-check svg { flex-shrink: 0; }

        /* ── Pillars ── */
        .ab-pillars-title {
          font-family: var(--font-display);
          font-size: 1.15rem;
          font-weight: 600;
          color: var(--ab-text-dark);
          margin-bottom: 1.25rem;
          line-height: 1.3;
        }
        .ab-pillars {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
        }
        .ab-pillar {
          padding: 1.35rem 1.15rem;
          border-radius: 12px;
          border: 1.5px solid var(--ab-border);
          background: var(--ab-card);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
          position: relative;
          overflow: hidden;
        }
        .ab-pillar::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: var(--pillar-accent, #2563eb);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
        .ab-pillar:hover {
          transform: translateY(-4px);
          box-shadow: var(--ab-shadow-hover);
          border-color: var(--pillar-accent, #2563eb);
        }
        .ab-pillar:hover::before { transform: scaleX(1); }

        .ab-pillar-icon {
          width: 40px; height: 40px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          margin-bottom: 0.85rem;
          color: var(--pillar-accent, #2563eb);
          background: var(--pillar-bg, rgba(37,99,235,0.07));
          border: 1px solid var(--pillar-border, rgba(37,99,235,0.15));
          transition: transform 0.2s;
        }
        .ab-pillar:hover .ab-pillar-icon { transform: scale(1.1) rotate(-4deg); }

        .ab-pillar h5 {
          font-family: var(--font-display);
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--ab-text-dark);
          margin-bottom: 0.45rem;
          line-height: 1.3;
        }
        .ab-pillar p {
          font-family: var(--font-body);
          font-size: 0.83rem;
          font-weight: 400;
          color: var(--ab-text-body);
          line-height: 1.65;
          margin: 0;
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .ab-pillars { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .ab-card-wrap    { margin-top: -28px; }
          .ab-card-body    { padding: 1.5rem 1rem; }
          .ab-breadcrumb   { padding: 0.75rem 1rem; }
          .ab-pillars      { grid-template-columns: 1fr; }
          .ab-stats-panel  { flex: 1 1 100%; }
          .ab-quote-divider { display: none; }
          .ab-quote-band   { padding: 1.5rem 1.25rem; }
        }
        @media (max-width: 400px) {
          .ab-hero-title   { font-size: 1.75rem; }
        }
      `}</style>

      <div className="ab-page">

        {/* ── Hero Banner ── */}
        <div className="ab-hero" aria-label="About Us">
          <div className="ab-hero-glow2" aria-hidden="true" />
          <div className="ab-hero-content">
            <span className="ab-hero-eyebrow">Who We Are</span>
            <h1 className="ab-hero-title">
              About <span>Skywise</span>
            </h1>
            <p className="ab-hero-sub">
              Building intelligent, scalable, and secure software solutions for
              mining, logistics, and beyond.
            </p>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="ab-container">
          <div className="ab-card-wrap">

            {/* Breadcrumb */}
            <nav className="ab-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaChevronRight aria-hidden="true" />
              <span>About Us</span>
            </nav>

            {/* Card body */}
            <div className="ab-card-body">

              {/* ── Our Company ── */}
              <div className="ab-eyebrow">Our Company</div>
              <h2 className="ab-section-title">Who We Are</h2>
              <div className="ab-underline" />

              <div className="ab-intro-grid">

                {/* Text paragraphs + tech tags */}
                <div className="ab-intro-text">
                  {PARAGRAPHS.map(({ id, content }) => (
                    <p key={id}>{content}</p>
                  ))}
                  <div className="ab-tech-strip" style={{ marginTop: "1.5rem" }}>
                    {TECH_TAGS.map(({ id, icon, label }) => (
                      <span key={id} className="ab-tech-tag">
                        {icon} {label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stats panel */}
                <div className="ab-stats-panel">
                  {STATS.map(({ id, value, label }) => (
                    <div key={id} className="ab-stat">
                      <div className="ab-stat-value">{value}</div>
                      <div className="ab-stat-label">{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Quote / highlight band ── */}
              <div className="ab-quote-band">
                <div className="ab-quote-band-inner">
                  <div className="ab-quote-left">
                    <h4>
                      <strong>Customer-Centric.</strong>
                      Growth-Driven.
                      <br />Powered by IT &amp; AI.
                    </h4>
                  </div>
                  <div className="ab-quote-divider" aria-hidden="true" />
                  <div className="ab-quote-right">
                    <p>
                      Since inception, {COMPANY} has pioneered{" "}
                      <strong>IT and AI-powered solutions</strong>, leveraging
                      cutting-edge technologies to deliver intelligent, scalable,
                      and future-ready innovations for diverse business needs.
                    </p>
                    {QUOTE_CHECKS.map(({ id, text }) => (
                      <div key={id} className="ab-quote-check">
                        <FaCheckCircle aria-hidden="true" />
                        {text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Core Pillars ── */}
              <div className="ab-pillars-title">What Drives Us</div>
              <div className="ab-pillars">
                {PILLARS.map(({ id, icon, title, desc, accent, bg }) => (
                  <div
                    key={id}
                    className="ab-pillar"
                    style={{
                      "--pillar-accent": accent,
                      "--pillar-bg":     bg,
                      "--pillar-border": accent + "33",
                    }}
                  >
                    <div className="ab-pillar-icon" aria-hidden="true">{icon}</div>
                    <h5>{title}</h5>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {/* ── VMC & Leadership ── */}
          <VMC />
          <Leader />
        </div>

        <WhatsApp />
      </div>
    </>
  );
}

export default About;